import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, API } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { Seo } from "@/components/Seo";
import { DriftSavingsHero as TrustSavingsHero } from "@/components/DriftSavingsHero";
import {
  ShieldCheck, Lock, FileText, Download, CheckCircle2, CircleDashed,
  Clock, ArrowRight, Loader2, Server, Accessibility, Database,
} from "lucide-react";

const STATUS = {
  implemented: { label: "In place", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", Icon: CheckCircle2 },
  partial: { label: "In progress", cls: "bg-amber-50 text-amber-700 ring-amber-600/20", Icon: Clock },
  planned: { label: "On roadmap", cls: "bg-slate-100 text-slate-600 ring-slate-500/20", Icon: CircleDashed },
};

// Resilient fallback so the Trust Centre always lists the documents even if
// GET /api/legal hiccups or returns an incomplete set.
const FALLBACK_LEGAL = [
  { slug: "privacy-notice", label: "Privacy Notice" },
  { slug: "dpa", label: "Data Processing Agreement" },
  { slug: "terms-of-service", label: "Terms of Service" },
  { slug: "acceptable-use", label: "Acceptable Use Policy" },
  { slug: "sla", label: "Service Level Agreement" },
  { slug: "ai-governance", label: "AI Governance Statement" },
];

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.planned;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.cls}`}
      data-testid={`trust-status-${status}`}>
      <s.Icon size={12} /> {s.label}
    </span>
  );
}

function SecurityQuestionnaire() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        ...form,
        kind: "security",
        source: "trust-questionnaire",
        message: "Requested security questionnaire / SIG-Lite responses via Trust Centre",
      });
      setSent(true);
    } catch {
      setSent(true); // fail soft — never block the visitor
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl px-6 pb-12">
      <div className="rounded-2xl border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.06] to-transparent p-7" data-testid="trust-questionnaire">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-ek-purple" />
          <h2 className="text-lg font-semibold">{t("trust.questionnaireTitle")}</h2>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          {t("trust.questionnaireBlurb")}
        </p>
        {sent ? (
          <p className="mt-5 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700" data-testid="trust-questionnaire-success">
            <CheckCircle2 size={16} /> {t("trust.questionnaireSuccess")}
          </p>
        ) : (
          <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-4" data-testid="trust-questionnaire-form">
            <input required placeholder={t("trust.questionnaireNameLabel")} aria-label={t("trust.questionnaireNameLabel")} value={form.name} onChange={(e) => set("name", e.target.value)}
              className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-q-name" />
            <input required type="email" placeholder={t("trust.questionnaireEmailLabel")} aria-label={t("trust.questionnaireEmailLabel")} value={form.email} onChange={(e) => set("email", e.target.value)}
              className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-q-email" />
            <input required placeholder={t("trust.questionnaireCompanyLabel")} aria-label={t("trust.questionnaireCompanyLabel")} value={form.company} onChange={(e) => set("company", e.target.value)}
              className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-q-company" />
            <button type="submit" disabled={busy} className="ek-btn flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold" data-testid="trust-q-submit">
              {busy ? <Loader2 size={15} className="animate-spin" /> : t("trust.questionnaireSubmit")}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function VpatRequest() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const currentLang = (i18n.language || "en").slice(0, 2);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        ...form,
        kind: "accessibility",
        source: "trust-vpat",
        message: "Requested VPAT / WCAG 2.1 AA conformance documentation via Trust Centre",
        locale: currentLang,
      });
      setSent(true);
    } catch {
      setSent(true); // fail soft — never block the visitor
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700" data-testid="trust-vpat-success">
        <CheckCircle2 size={16} /> {t("trust.vpatSuccess")}
      </p>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-ek-purple/30 bg-white px-4 py-2.5 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5"
        data-testid="trust-accessibility-vpat">
        {t("trust.vpatCta")} <ArrowRight size={15} />
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="grid w-full gap-3 sm:max-w-md sm:grid-cols-2" data-testid="trust-vpat-form">
      <input required placeholder={t("trust.vpatNameLabel")} aria-label={t("trust.vpatNameLabel")} value={form.name} onChange={(e) => set("name", e.target.value)}
        className="rounded-lg border border-black/10 px-3 py-2.5 text-sm sm:col-span-2" data-testid="trust-vpat-name" />
      <input required type="email" placeholder={t("trust.vpatEmailLabel")} aria-label={t("trust.vpatEmailLabel")} value={form.email} onChange={(e) => set("email", e.target.value)}
        className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-vpat-email" />
      <input required placeholder={t("trust.vpatCompanyLabel")} aria-label={t("trust.vpatCompanyLabel")} value={form.company} onChange={(e) => set("company", e.target.value)}
        className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-vpat-company" />
      <button type="submit" disabled={busy}
        className="ek-btn flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold sm:col-span-2" data-testid="trust-vpat-submit">
        {busy ? <Loader2 size={15} className="animate-spin" /> : t("trust.vpatSubmit")}
      </button>
    </form>
  );
}

function EvidencePack() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", nda_ack: false, include_snapshot: true });
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const triggerDownload = async (tk, snap) => {
    // We fetch the ZIP as a blob and download via an object URL instead
    // of a simple anchor click. Reasons:
    //   1. After `await api.post(...)` the user-gesture context is lost;
    //      some browsers (notably Chromium on Cloudflare-fronted origins)
    //      then silently ABORT the second GET and no file is saved.
    //   2. Blob download surfaces network / rate-limit errors so we can
    //      show them to the user instead of leaving them staring at a
    //      false "success" state with no file on disk.
    const includeSnap = snap ? 1 : 0;
    const url = `${API}/trust/evidence-pack.zip?token=${encodeURIComponent(tk)}&include_snapshot=${includeSnap}`;
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) {
      let detail = null;
      try {
        detail = (await resp.json())?.detail;
      } catch { /* non-JSON body */ }
      throw new Error(detail || `Download failed (HTTP ${resp.status}).`);
    }
    const blob = await resp.blob();
    // Prefer the server-supplied filename from Content-Disposition; fall
    // back to a sensible client-side default when the header is stripped
    // by an intermediary.
    const cd = resp.headers.get("content-disposition") || "";
    const match = /filename="?([^";]+)"?/i.exec(cd);
    const filename = match ? match[1] : `erio-evidence-pack.zip`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick — some browsers still need the URL live
    // for the download to complete.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/trust/evidence-pack/request", {
        name: form.name,
        email: form.email,
        company: form.company,
        nda_ack: form.nda_ack,
        include_snapshot: form.include_snapshot,
      });
      await triggerDownload(data.token, form.include_snapshot);
      setToken(data.token);
    } catch (err) {
      const apiDetail = err?.response?.data?.detail;
      const msg =
        (typeof apiDetail === "string" && apiDetail) ||
        err?.message ||
        "Sorry, something went wrong. Please try again.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (token) {
    return (
      <section className="mx-auto max-w-5xl px-6 pb-12">
        <div className="rounded-2xl border border-emerald-200/60 bg-emerald-50/40 p-7" data-testid="trust-evidence-success">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={20} className="mt-0.5 text-emerald-600" />
            <div>
              <h2 className="text-lg font-semibold text-emerald-900">{t("trust.evidencePackTitle")}</h2>
              <p className="mt-2 max-w-2xl text-sm text-emerald-900/80">{t("trust.evidencePackSuccess")}</p>
              <button
                onClick={() => triggerDownload(token, form.include_snapshot).catch((e) => setError(e?.message || "Download failed."))}
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-emerald-600/30 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                data-testid="trust-evidence-retry">
                <Download size={15} /> {t("trust.evidencePackRetry")}
              </button>
              {error && (
                <p className="mt-3 text-sm text-rose-700" data-testid="trust-evidence-error">{error}</p>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-6 pb-12">
      <div className="rounded-2xl border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.08] to-transparent p-7" data-testid="trust-evidence-pack">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple">
            <Download size={22} />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{t("trust.evidencePackTitle")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">{t("trust.evidencePackBlurb")}</p>

            {!open ? (
              <button
                onClick={() => setOpen(true)}
                className="ek-btn mt-5 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold"
                data-testid="trust-evidence-cta">
                {t("trust.evidencePackCta")} <ArrowRight size={15} />
              </button>
            ) : (
              <form onSubmit={submit} className="mt-5 grid gap-3 sm:max-w-lg" data-testid="trust-evidence-form">
                <input required placeholder={t("trust.evidencePackNameLabel")} aria-label={t("trust.evidencePackNameLabel")} value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-evidence-name" />
                <input required type="email" placeholder={t("trust.evidencePackEmailLabel")} aria-label={t("trust.evidencePackEmailLabel")} value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-evidence-email" />
                <input required placeholder={t("trust.evidencePackCompanyLabel")} aria-label={t("trust.evidencePackCompanyLabel")} value={form.company} onChange={(e) => set("company", e.target.value)}
                  className="rounded-lg border border-black/10 px-3 py-2.5 text-sm" data-testid="trust-evidence-company" />

                <label className="mt-1 flex items-start gap-2 text-sm text-ek-ink/75">
                  <input
                    type="checkbox"
                    checked={form.include_snapshot}
                    onChange={(e) => set("include_snapshot", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-black/20 text-ek-purple focus:ring-ek-purple"
                    data-testid="trust-evidence-include-snapshot"
                  />
                  <span>{t("trust.evidencePackIncludeSnapshot")}</span>
                </label>

                <label className="flex items-start gap-2 text-sm text-ek-ink/75">
                  <input
                    required
                    type="checkbox"
                    checked={form.nda_ack}
                    onChange={(e) => set("nda_ack", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-black/20 text-ek-purple focus:ring-ek-purple"
                    data-testid="trust-evidence-nda"
                  />
                  <span>{t("trust.evidencePackNdaAck")}</span>
                </label>

                {error && (
                  <p className="text-sm text-rose-700" data-testid="trust-evidence-error">{error}</p>
                )}

                <button type="submit" disabled={busy}
                  className="ek-btn flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                  data-testid="trust-evidence-submit">
                  {busy ? <><Loader2 size={15} className="animate-spin" /> {t("trust.evidencePackSending")}</> : <>{t("trust.evidencePackSubmit")} <Download size={15} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Trust() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [legal, setLegal] = useState(FALLBACK_LEGAL);

  const currentLang = (i18n.language || "en").slice(0, 2);
  useEffect(() => {
    api.get(`/public/trust?lang=${currentLang}`).then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/legal")
      .then((r) => {
        const docs = r.data?.docs || [];
        // Fall back to the known set if the API ever returns an incomplete list.
        setLegal(docs.length >= FALLBACK_LEGAL.length ? docs : FALLBACK_LEGAL);
      })
      .catch(() => setLegal(FALLBACK_LEGAL));
  }, [currentLang]);

  return (
    <PublicShell active="/trust">
      <Seo title={t("trust.seoTitle")} path="/trust" localized
        description={t("trust.seoDescription")} />

      <section className="border-b border-black/5 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-ek-purple">{t("trust.eyebrow")}</span>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">{t("trust.title")}</h1>
          <p className="mt-4 max-w-2xl text-base text-ek-ink/70">
            {t("trust.blurb")}
          </p>
          {data && data.standards && (
            <div className="mt-7 flex flex-wrap gap-2" data-testid="trust-standards">
              {data.standards.map((s) => (
                <span key={s.name} className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-ek-canvas px-3 py-1.5 text-sm">
                  <ShieldCheck size={14} className="text-ek-purple" />
                  <span className="font-medium">{s.name}</span>
                  <span className="text-ek-ink/65">·</span>
                  <span className="text-ek-ink/70">{s.state}</span>
                </span>
              ))}
            </div>
          )}
          <TrustLiveIntegrationsBadge />
        </div>
      </section>

      {data === null ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-ek-purple" /></div>
      ) : (
        <>
          {/* Readiness headline */}
          {typeof data.readiness_pct === "number" && (
            <section className="mx-auto max-w-5xl px-6 pt-12">
              <div className="flex flex-col gap-4 rounded-2xl bg-ek-ink px-8 py-7 text-white sm:flex-row sm:items-center sm:justify-between"
                data-testid="trust-readiness">
                <div>
                  <p className="text-sm text-white/60">{t("trust.readinessLabel")}</p>
                  <p className="mt-1 text-3xl font-semibold">{data.readiness_pct}%</p>
                </div>
                <p className="max-w-md text-sm text-white/70">
                  {data.control_counts?.implemented} {t("trust.readinessBlurbPart1")} {data.control_total} {t("trust.readinessBlurbPart2")}
                </p>
              </div>
            </section>
          )}

          {/* Drift £ caught + Saved by remediation — gross / net pair */}
          <TrustSavingsHero />

          {/* Security posture */}
          <section className="mx-auto max-w-5xl px-6 py-12">
            <h2 className="flex items-center gap-2 text-lg font-semibold"><Lock size={18} className="text-ek-purple" /> {t("trust.postureTitle")}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="trust-posture">
              {(data.posture || []).map((p) => (
                <div key={p.area} className="rounded-xl border border-black/5 bg-white p-5"
                  data-testid={`trust-control-${p.area.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-semibold text-ek-ink">{p.area}</h3>
                    <StatusChip status={p.status} />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ek-ink/70">{p.summary}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Accessibility conformance */}
          <section id="trust-accessibility" className="mx-auto max-w-5xl px-6 pb-12 scroll-mt-24" data-testid="trust-accessibility">
            <div className="flex flex-col gap-4 rounded-2xl border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.06] to-transparent p-7 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple">
                  <Accessibility size={22} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold">{t("trust.accessibilityTitle")}</h2>
                    <StatusChip status="implemented" />
                  </div>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ek-ink/70">
                    {t("trust.accessibilityBlurb")}
                  </p>
                </div>
              </div>
              <div className="sm:min-w-[18rem] sm:max-w-md">
                <VpatRequest />
              </div>
            </div>
          </section>

          {/* Engineering quality */}
          <section id="trust-engineering" className="mx-auto max-w-5xl px-6 pb-12 scroll-mt-24" data-testid="trust-engineering">
            <div className="flex flex-col gap-4 rounded-2xl border border-emerald-200/40 bg-gradient-to-br from-emerald-50/40 to-transparent p-7 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                  <ShieldCheck size={22} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold">{t("trust.engineeringTitle")}</h2>
                    <StatusChip status="implemented" />
                  </div>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ek-ink/70">
                    {t("trust.engineeringBlurb")}
                  </p>
                </div>
              </div>
              <div className="sm:min-w-[14rem] sm:max-w-md sm:text-right">
                <a
                  href="/tests.svg"
                  data-testid="trust-tests-badge-link"
                  className="inline-block transition-opacity hover:opacity-80"
                  title="Live automated test count — refreshed each release"
                >
                  <img
                    src="/tests.svg"
                    alt="Automated tests passing"
                    className="h-6 w-auto"
                    data-testid="trust-tests-badge"
                  />
                </a>
              </div>
            </div>
          </section>

          {/* Tenant-scope verified */}
          <section id="trust-tenant-isolation" className="mx-auto max-w-5xl px-6 pb-12 scroll-mt-24" data-testid="trust-tenant-isolation">
            <div className="flex flex-col gap-4 rounded-2xl border border-sky-200/50 bg-gradient-to-br from-sky-50/50 to-transparent p-7 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <Database size={22} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold">{t("trust.tenantIsolationTitle")}</h2>
                    <StatusChip status="implemented" />
                  </div>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ek-ink/70">
                    {t("trust.tenantIsolationBlurb")}
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-ek-ink/65">
                    <li><span className="font-semibold text-ek-ink/80">iter126</span> — proxy auto-scoping</li>
                    <li><span className="font-semibold text-ek-ink/80">iter129</span> — function-aware CI lint</li>
                    <li><span className="font-semibold text-ek-ink/80">iter130</span> — dual-source playbooks</li>
                  </ul>
                  <p className="mt-3 text-xs text-ek-ink/55">
                    Procurement & questionnaire automations: machine-readable equivalent at{" "}
                    <a
                      href={`${API}/well-known/security-trust.json`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="trust-well-known-link"
                      className="font-medium text-ek-purple underline-offset-2 hover:underline"
                    >
                      /api/well-known/security-trust.json
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="sm:min-w-[14rem] sm:max-w-md sm:text-right">
                <span
                  data-testid="trust-tenant-scope-badge"
                  className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3.5 py-1.5 text-xs font-semibold text-sky-800 ring-1 ring-inset ring-sky-600/20"
                  title="Static-AST lint blocks any PR that regresses tenant scoping"
                >
                  <CheckCircle2 size={14} />
                  Tenant isolation · iter126 / 129 / 130
                </span>
              </div>
            </div>
          </section>

          {/* Tenancy architecture — the 3-paragraph procurement answer */}
          <section id="trust-tenancy-architecture" className="mx-auto max-w-5xl px-6 pb-12 scroll-mt-24" data-testid="trust-tenancy-architecture">
            <div className="rounded-2xl border border-ek-purple/15 bg-white/60 p-7">
              <div className="flex flex-wrap items-center gap-2">
                <Lock size={18} className="text-ek-purple" />
                <h2 className="text-lg font-semibold">{t("trust.tenancyTitle")}</h2>
              </div>
              <div className="prose prose-sm mt-4 max-w-none text-ek-ink/75">
                <p>{t("trust.tenancyPara1")}</p>
                <p>{t("trust.tenancyPara2")}</p>
                <p>{t("trust.tenancyPara3")}</p>
              </div>
            </div>
          </section>

          {/* Sub-processors */}
          <section className="mx-auto max-w-5xl px-6 pb-12">
            <h2 className="flex items-center gap-2 text-lg font-semibold"><Server size={18} className="text-ek-purple" /> {t("trust.subprocessorsTitle")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
              {t("trust.subprocessorsBlurb")}
            </p>
            <div className="mt-5 overflow-hidden rounded-xl border border-black/5 bg-white" data-testid="trust-subprocessors">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/5 bg-ek-canvas text-left text-xs uppercase tracking-wide text-ek-ink/70">
                    <th className="px-4 py-3 font-semibold">{t("trust.subprocessorsHeaderName")}</th>
                    <th className="px-4 py-3 font-semibold">{t("trust.subprocessorsHeaderPurpose")}</th>
                    <th className="hidden px-4 py-3 font-semibold sm:table-cell">{t("trust.subprocessorsHeaderData")}</th>
                    <th className="px-4 py-3 font-semibold">{t("trust.subprocessorsHeaderRegion")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.subprocessors || []).map((sp) => (
                    <tr key={sp.name} className="border-b border-black/5 last:border-0">
                      <td className="px-4 py-3 font-medium text-ek-ink">{sp.name}</td>
                      <td className="px-4 py-3 text-ek-ink/65">{sp.purpose}</td>
                      <td className="hidden px-4 py-3 text-ek-ink/70 sm:table-cell">{sp.data}</td>
                      <td className="px-4 py-3 text-ek-ink/70">{sp.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Legal documents */}
          <section className="mx-auto max-w-5xl px-6 pb-14">
            <h2 className="flex items-center gap-2 text-lg font-semibold"><FileText size={18} className="text-ek-purple" /> {t("trust.legalTitle")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
              {t("trust.legalBlurb")}
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="trust-legal">
              {legal.map((d) => (
                <a key={d.slug} href={`${API}/legal/${d.slug}`} target="_blank" rel="noreferrer"
                  className="flex items-center justify-between rounded-xl border border-black/5 bg-white px-5 py-4 transition-shadow hover:shadow-md"
                  data-testid={`trust-doc-${d.slug}`}>
                  <span className="flex items-center gap-3 text-sm font-medium text-ek-ink">
                    <FileText size={16} className="text-ek-purple" /> {d.label}
                  </span>
                  <Download size={15} className="text-ek-ink/65" />
                </a>
              ))}
            </div>
          </section>

          {/* Security questionnaire capture */}
          <SecurityQuestionnaire />

          {/* Evidence pack — NDA-gated download */}
          <EvidencePack />

          {/* CTA */}
          <section className="mx-auto max-w-5xl px-6 pb-16">
            <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-ek-ink px-8 py-7 text-white sm:flex-row">
              <div>
                <p className="text-lg font-semibold">{t("trust.ctaTitle")}</p>
                <p className="mt-1 text-sm text-white/60">{t("trust.ctaSubtitle")}</p>
              </div>
              <div className="flex gap-3">
                <Link to="/demo" className="flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-ek-ink hover:bg-white/90" data-testid="trust-cta-demo">
                  {t("trust.ctaBookDemo")} <ArrowRight size={15} />
                </Link>
                <a href="mailto:info@ekselens.com" className="flex items-center gap-2 rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10" data-testid="trust-cta-contact">
                  {t("trust.ctaContact")}
                </a>
              </div>
            </div>
          </section>
        </>
      )}
    </PublicShell>
  );
}


/**
 * TrustLiveIntegrationsBadge — surfaces the same "Live data freshness" signal
 * as the marketing strip, but framed for procurement: "Real connector traffic
 * happening right now, no theatre". Hides on zero so we never embarrass.
 */
function TrustLiveIntegrationsBadge() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/connector-freshness`)
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (mounted) setData(j); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  if (!data || !data.most_recent_sync_at || (data.total_synced_plans || 0) <= 0) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(data.most_recent_sync_at).getTime()) / 60_000));
  const ago = mins < 60 ? `${mins}m` : mins < 24 * 60 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / (24 * 60))}d`;
  return (
    <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-1.5 text-sm"
         data-testid="trust-live-integrations">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span className="font-medium text-emerald-900">
        <span data-testid="trust-live-count">{data.live_integrations}</span> live integration{data.live_integrations === 1 ? "" : "s"}
      </span>
      <span className="text-ek-ink/40">·</span>
      <span className="text-emerald-900">{data.total_synced_plans.toLocaleString()} plans synced</span>
      <span className="text-ek-ink/40">·</span>
      <span className="text-emerald-900">last refresh {ago} ago</span>
    </div>
  );
}
